import React, { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { ShieldCheck, ExternalLink, Save, Check, AlertTriangle } from "lucide-react";

/**
 * BusinessPrivacyEditor — owner edits the data used to auto-generate the
 * customer-facing privacy policy. The form is pre-filled with sensible
 * defaults (business name/address) on first open.
 */
export default function BusinessPrivacyEditor({ businessId }) {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  async function load() {
    setLoading(true);
    const { data } = await api.get(`/businesses/${businessId}/privacy-settings`);
    setForm(data);
    setLoading(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessId]);

  async function save(e) {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const payload = {
        holder_name: form.holder_name.trim(),
        holder_address: form.holder_address.trim(),
        holder_vat: form.holder_vat.trim(),
        holder_email: form.holder_email.trim().toLowerCase(),
        holder_phone: form.holder_phone?.trim() || null,
        holder_oggetto: form.holder_oggetto.trim(),
        retention_years: parseInt(form.retention_years, 10) || 10,
        custom_addendum: form.custom_addendum?.trim() || null,
      };
      await api.put(`/businesses/${businessId}/privacy-settings`, payload);
      setMsg({ type: "ok", text: "Informativa salvata e pubblicata ✓" });
      await load();
    } catch (e2) {
      setMsg({ type: "err", text: formatErr(e2.response?.data?.detail) || "Errore" });
    } finally {
      setBusy(false);
    }
  }

  if (loading || !form) return <div className="p-8 text-sm text-zinc-500 dark:text-zinc-400">Caricamento…</div>;

  const publicUrl = `${window.location.origin}/p/${businessId}/privacy`;

  return (
    <div className="max-w-3xl space-y-5" data-testid="business-privacy-editor">
      <div>
        <h3 className="font-display text-xl font-medium flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-emerald-600" /> Informativa Privacy clienti
        </h3>
        <p className="text-sm text-zinc-500 mt-1 dark:text-zinc-400">
          Compila i dati della tua attività: Prenotoo genererà automaticamente un'informativa GDPR-compliant
          pubblica, linkata in ogni pagina di prenotazione. Puoi modificarla ogni volta che vuoi.
        </p>
      </div>

      <div className={`rounded-2xl px-4 py-3 text-sm ${form.completed ? "bg-emerald-50 border border-emerald-200 text-emerald-900 dark:bg-emerald-950/40 dark:border-emerald-800/50 dark:text-emerald-200" : "bg-amber-50 border border-amber-200 text-amber-900 dark:bg-amber-950/40 dark:border-amber-800/50 dark:text-amber-200"}`}>
        {form.completed ? (
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="inline-flex items-center gap-2"><Check className="h-4 w-4" /> Privacy pubblicata. URL: <code className="text-xs bg-emerald-100 px-2 py-0.5 rounded dark:bg-emerald-950/40">/p/{businessId.slice(0, 8)}.../privacy</code></span>
            <a href={publicUrl} target="_blank" rel="noreferrer" className="text-xs font-semibold inline-flex items-center gap-1 hover:underline" data-testid="biz-privacy-preview">
              Anteprima <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        ) : (
          <span className="inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Non ancora compilata. I tuoi clienti vedranno un placeholder generico.</span>
        )}
      </div>

      <form onSubmit={save} className="card-soft p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Ragione sociale / Nome attività *">
            <input required maxLength={200} value={form.holder_name} onChange={(e) => setForm({ ...form, holder_name: e.target.value })} className="input-soft" data-testid="biz-privacy-name" />
          </Field>
          <Field label="P.IVA / Codice Fiscale *">
            <input required maxLength={40} value={form.holder_vat} onChange={(e) => setForm({ ...form, holder_vat: e.target.value })} className="input-soft" data-testid="biz-privacy-vat" />
          </Field>
        </div>

        <Field label="Indirizzo completo *">
          <input required maxLength={200} value={form.holder_address} onChange={(e) => setForm({ ...form, holder_address: e.target.value })} className="input-soft" placeholder="Via, n°, CAP, Città (PR)" data-testid="biz-privacy-address" />
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Email per richieste privacy *">
            <input type="email" required value={form.holder_email} onChange={(e) => setForm({ ...form, holder_email: e.target.value })} className="input-soft" data-testid="biz-privacy-email" />
          </Field>
          <Field label="Telefono (opzionale)">
            <input value={form.holder_phone || ""} onChange={(e) => setForm({ ...form, holder_phone: e.target.value })} className="input-soft" data-testid="biz-privacy-phone" />
          </Field>
        </div>

        <Field label="Oggetto attività *" hint="1-2 righe descrittive — comparirà nell'informativa.">
          <textarea required minLength={5} maxLength={400} rows={2} value={form.holder_oggetto} onChange={(e) => setForm({ ...form, holder_oggetto: e.target.value })} className="input-soft h-auto py-3 resize-none" placeholder="Es. Centro estetico professionale specializzato in trattamenti viso e corpo" data-testid="biz-privacy-oggetto" />
        </Field>

        <Field label="Anni di conservazione dati" hint="Default 10 anni (obbligo fiscale art. 2220 c.c.).">
          <select value={form.retention_years} onChange={(e) => setForm({ ...form, retention_years: parseInt(e.target.value, 10) })} className="input-soft" data-testid="biz-privacy-retention">
            {[5, 7, 10, 15, 20].map((n) => <option key={n} value={n}>{n} anni</option>)}
          </select>
        </Field>

        <Field label="Note aggiuntive (opzionale)" hint="Es. dichiarazioni specifiche del tuo settore. Comparirà come sezione separata in fondo.">
          <textarea maxLength={3000} rows={3} value={form.custom_addendum || ""} onChange={(e) => setForm({ ...form, custom_addendum: e.target.value })} className="input-soft h-auto py-3 resize-none" data-testid="biz-privacy-addendum" />
        </Field>

        {msg && (
          <div className={`text-sm rounded-2xl px-4 py-3 inline-flex items-start gap-2 ${msg.type === "ok" ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300" : "bg-rose-50 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300"}`}>
            {msg.type === "ok" ? <Check className="h-4 w-4 mt-0.5" /> : <AlertTriangle className="h-4 w-4 mt-0.5" />}
            {msg.text}
          </div>
        )}

        <button type="submit" disabled={busy} className="btn-accent" data-testid="biz-privacy-save">
          <Save className="h-4 w-4 mr-2" /> {busy ? "Salvo…" : "Salva e pubblica"}
        </button>
      </form>
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">{label}</span>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="text-[11px] text-zinc-500 mt-1 dark:text-zinc-400">{hint}</p>}
    </label>
  );
}
